import { useState } from 'react';
import {
  MOTIVOS_DE_BLOQUEO, type Agenda as DatosAgenda, type Bloqueo, type MotivoDeBloqueo, type TurnoDeAgenda,
} from '@medchoice/shared';
import { ApiError } from '../../api/client';
import { consultorio } from '../../api/consultorio';
import { usePedido } from '../../api/usePedido';
import { irA } from '../../router';
import {
  AvisoError, Boton, Cargando, Entrada, EstadoVacio, FalloDeCarga, Icono, InsigniaEstadoTurno, Modal, Pestanas,
  Selector, Tarjeta, useAvisos,
} from '../../ui';
import { SeccionPanel } from '../../ui/layout/PaginaPanel';
import { diaDeLaSemana, fechaCorta, fechaLarga, hoyIso, lunesDe, pesos, sumarDias } from '../../ui/format';
import { AvisoDeVerificacion, nombreDe } from './piezas';

type Vista = 'dia' | 'semana' | 'mes' | 'lista';

const VISTAS = [
  { id: 'dia', texto: 'Día' }, { id: 'semana', texto: 'Semana' }, { id: 'mes', texto: 'Mes' }, { id: 'lista', texto: 'Lista' },
] as const;
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const TEXTO_MOTIVO: Record<MotivoDeBloqueo, string> = {
  almuerzo: 'Almuerzo', reunion: 'Reunión', vacaciones: 'Vacaciones', otro: 'Otro motivo',
};

/** Alto en píxeles de cada media hora en las vistas de día y semana. */
const ALTO_MEDIA_HORA = 42;

const minutos = (hora: string) => Number(hora.slice(0, 2)) * 60 + Number(hora.slice(3, 5));
const conMayuscula = (texto: string) => texto.charAt(0).toUpperCase() + texto.slice(1);

function rango(vista: Vista, ancla: string): { desde: string; hasta: string; dias: string[] } {
  if (vista === 'dia') return { desde: ancla, hasta: ancla, dias: [ancla] };
  if (vista === 'mes') {
    const desde = `${ancla.slice(0, 7)}-01`;
    const hasta = sumarDias(`${sumarDias(desde, 32).slice(0, 7)}-01`, -1);
    return { desde, hasta, dias: [] };
  }
  const lunes = lunesDe(ancla);
  const dias = Array.from({ length: 6 }, (_, i) => sumarDias(lunes, i));
  return { desde: lunes, hasta: dias[5]!, dias };
}

const PASO: Record<Vista, number> = { dia: 1, semana: 7, mes: 31, lista: 7 };

export function Agenda() {
  const [vista, setVista] = useState<Vista>(() => (window.innerWidth < 1000 ? 'lista' : 'semana'));
  const [ancla, setAncla] = useState(hoyIso());
  const [abierto, setAbierto] = useState<TurnoDeAgenda | null>(null);
  const [bloqueando, setBloqueando] = useState(false);
  const { desde, hasta, dias } = rango(vista, ancla);
  const pedido = usePedido(() => consultorio.agenda(desde, hasta), [desde, hasta]);

  const mover = (sentido: number) => {
    const destino = sumarDias(ancla, sentido * PASO[vista]);
    setAncla(vista === 'mes' ? `${destino.slice(0, 7)}-01` : destino);
  };
  const titulo = {
    dia: conMayuscula(fechaLarga(ancla)),
    semana: `Semana del ${fechaCorta(desde)}`,
    lista: `Semana del ${fechaCorta(desde)}`,
    mes: conMayuscula(`${MESES[Number(ancla.slice(5, 7)) - 1]} de ${ancla.slice(0, 4)}`),
  }[vista];

  return (
    <SeccionPanel titulo="Agenda" descripcion="Tus turnos y los horarios que bloqueaste"
      acciones={<Boton tamano="chico" icono="ban" variante="secundario" onClick={() => setBloqueando(true)}>Bloquear horario</Boton>}>
      <div className="pila">
        <AvisoDeVerificacion />
        <div className="agenda__controles">
          <div className="agenda__navegacion">
            <Boton variante="icono" icono="chevron-left" aria-label="Anterior" onClick={() => mover(-1)} />
            <Boton variante="secundario" tamano="chico" onClick={() => setAncla(hoyIso())}>Hoy</Boton>
            <Boton variante="icono" icono="chevron-right" aria-label="Siguiente" onClick={() => mover(1)} />
            <h2>{titulo}</h2>
          </div>
          <Pestanas<Vista> etiqueta="Vista de la agenda" activa={vista} alElegir={setVista} pestanas={VISTAS} />
        </div>

        {pedido.estado === 'cargando' && !pedido.datos && <Cargando />}
        {pedido.estado === 'error' && <FalloDeCarga mensaje={pedido.error ?? ''} alReintentar={pedido.recargar} />}
        {pedido.datos && (
          <>
            {/* La clave sale de los datos cargados: el bloque vuelve a entrar cuando llega la
                semana nueva, no antes, así no se ve la anterior con el fundido. */}
            <div key={`${vista}-${pedido.datos.desde}`} className="entrada-pantalla">
              {(vista === 'dia' || vista === 'semana') && <Grilla agenda={pedido.datos} dias={dias} alAbrir={setAbierto} />}
              {vista === 'mes' && <Mes agenda={pedido.datos} alElegirDia={(d) => { setAncla(d); setVista('dia'); }} />}
              {vista === 'lista' && <Lista agenda={pedido.datos} dias={dias} alAbrir={setAbierto} />}
            </div>
            <Bloqueos bloqueos={pedido.datos.bloqueos} alCambiar={pedido.recargar} />
          </>
        )}
      </div>
      <ModalTurno turno={abierto} alCerrar={() => setAbierto(null)} />
      {bloqueando && <ModalBloqueo alCerrar={() => setBloqueando(false)} alGuardar={pedido.recargar} />}
    </SeccionPanel>
  );
}

function Grilla({ agenda, dias, alAbrir }: { agenda: DatosAgenda; dias: string[]; alAbrir: (t: TurnoDeAgenda) => void }) {
  const inicio = Math.min(...agenda.horarios.map((h) => minutos(h.hora_inicio)), 9 * 60);
  const fin = Math.max(...agenda.horarios.map((h) => minutos(h.hora_fin)), 19 * 60);
  const filas = Array.from({ length: (fin - inicio) / 30 }, (_, i) => inicio + i * 30);
  const top = (hora: string) => ((minutos(hora) - inicio) / 30) * ALTO_MEDIA_HORA;
  const hoy = hoyIso();

  return (
    <Tarjeta relleno={0}>
      <div className="grilla-agenda" style={{ gridTemplateColumns: `56px repeat(${dias.length}, minmax(120px, 1fr))` }}>
        <div className="grilla-agenda__esquina" />
        {dias.map((d) => (
          <div key={d} className={`grilla-agenda__dia${d === hoy ? ' grilla-agenda__dia--hoy' : ''}`}>
            <span>{diaDeLaSemana(d).slice(0, 3)}</span> <strong>{Number(d.slice(8, 10))}</strong>
          </div>
        ))}
        <div className="grilla-agenda__horas">
          {filas.map((m) => (
            <div key={m} style={{ height: ALTO_MEDIA_HORA }}>
              {m % 60 === 0 ? `${String(m / 60).padStart(2, '0')}:00` : ''}
            </div>
          ))}
        </div>
        {dias.map((d) => (
          <div key={d} className="grilla-agenda__columna" style={{ height: filas.length * ALTO_MEDIA_HORA }}>
            {agenda.bloqueos.filter((b) => d >= b.fecha_inicio && d <= b.fecha_fin).map((b) => (
              <div key={b.id_bloqueo} className="grilla-agenda__bloqueo"
                style={{ top: top(b.hora_inicio), height: top(b.hora_fin) - top(b.hora_inicio) }}>
                {TEXTO_MOTIVO[b.motivo]}
              </div>
            ))}
            {agenda.turnos.filter((t) => t.fecha === d).map((t) => {
              const alto = top(t.hora_fin) - top(t.hora_inicio) - 3;
              return (
                <button key={t.id_turno} type="button" onClick={() => alAbrir(t)}
                  className={`grilla-agenda__turno grilla-agenda__turno--${t.estado}`}
                  style={{ top: top(t.hora_inicio), height: alto }}
                  title={`${nombreDe(t.paciente)} · ${t.servicio} · ${t.hora_inicio}`}>
                  <strong>{alto < 40 ? `${t.hora_inicio} ${t.paciente.nombre}` : nombreDe(t.paciente)}</strong>
                  {alto >= 40 && <span>{t.hora_inicio} · {t.servicio}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </Tarjeta>
  );
}

function Mes({ agenda, alElegirDia }: { agenda: DatosAgenda; alElegirDia: (dia: string) => void }) {
  const primero = agenda.desde;
  const celdas = Array.from({ length: 42 }, (_, i) => sumarDias(lunesDe(primero), i))
    .filter((d, i) => i < 35 || d.slice(0, 7) === primero.slice(0, 7));
  const hoy = hoyIso();
  return (
    <Tarjeta relleno={12}>
      <div className="mes-agenda">
        {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((d) => <div key={d} className="mes-agenda__cabecera">{d}</div>)}
        {celdas.map((d) => {
          const turnos = agenda.turnos.filter((t) => t.fecha === d);
          const delMes = d.slice(0, 7) === primero.slice(0, 7);
          return (
            <button key={d} type="button" disabled={!delMes} onClick={() => alElegirDia(d)}
              className={`mes-agenda__dia${d === hoy ? ' mes-agenda__dia--hoy' : ''}`}>
              <span>{Number(d.slice(8, 10))}</span>
              {delMes && turnos.length > 0 && <small>{turnos.length} {turnos.length === 1 ? 'turno' : 'turnos'}</small>}
            </button>
          );
        })}
      </div>
    </Tarjeta>
  );
}

function Lista({ agenda, dias, alAbrir }: { agenda: DatosAgenda; dias: string[]; alAbrir: (t: TurnoDeAgenda) => void }) {
  if (agenda.turnos.length === 0) {
    return <Tarjeta><EstadoVacio icono="calendar" titulo="Semana libre" cuerpo="No tenés turnos reservados en estos días." /></Tarjeta>;
  }
  return (
    <div className="pila">
      {dias.map((d) => {
        const turnos = agenda.turnos.filter((t) => t.fecha === d);
        if (turnos.length === 0) return null;
        return (
          <section key={d} className="lista-agenda">
            <h3>{conMayuscula(fechaLarga(d))}</h3>
            {turnos.map((t) => (
              <button key={t.id_turno} type="button" className="fila-turno" onClick={() => alAbrir(t)}>
                <span className="fila-turno__hora">{t.hora_inicio}</span>
                <span className="fila-turno__datos">
                  <strong>{nombreDe(t.paciente)}</strong>
                  <span>{t.servicio} · {pesos(t.precio)}</span>
                </span>
                <InsigniaEstadoTurno estado={t.estado} />
              </button>
            ))}
          </section>
        );
      })}
    </div>
  );
}

function Bloqueos({ bloqueos, alCambiar }: { bloqueos: Bloqueo[]; alCambiar: () => void }) {
  const { mostrarAviso } = useAvisos();
  if (bloqueos.length === 0) return null;
  const liberar = async (b: Bloqueo) => {
    try {
      await consultorio.desbloquear(b.id_bloqueo);
      mostrarAviso({ tono: 'exito', titulo: 'Horario liberado', cuerpo: 'Ya se puede reservar en ese rango.' });
      alCambiar();
    } catch (e) {
      mostrarAviso({ tono: 'error', titulo: 'No pudimos liberar el horario', cuerpo: e instanceof ApiError ? e.message : undefined });
    }
  };
  return (
    <Tarjeta>
      <h3 className="titulo-tarjeta">Horarios bloqueados</h3>
      <ul className="lista-bloqueos">
        {bloqueos.map((b) => (
          <li key={b.id_bloqueo}>
            <Icono nombre="ban" tamano={16} />
            <span>
              {b.fecha_inicio === b.fecha_fin ? fechaCorta(b.fecha_inicio) : `${fechaCorta(b.fecha_inicio)} al ${fechaCorta(b.fecha_fin)}`}
              {' · '}{b.hora_inicio} a {b.hora_fin} · {TEXTO_MOTIVO[b.motivo]}
            </span>
            <Boton variante="fantasma" tamano="chico" onClick={() => void liberar(b)}>Liberar</Boton>
          </li>
        ))}
      </ul>
    </Tarjeta>
  );
}

function ModalTurno({ turno, alCerrar }: { turno: TurnoDeAgenda | null; alCerrar: () => void }) {
  return (
    <Modal abierto={turno !== null} alCerrar={alCerrar} titulo={turno ? nombreDe(turno.paciente) : ''}
      pie={<>
        <Boton variante="secundario" onClick={alCerrar}>Cerrar</Boton>
        <Boton onClick={() => turno && irA(`/pro/turno/${turno.id_turno}`)}>Ver el turno</Boton>
      </>}>
      {turno && (
        <dl className="datos-modal">
          <div><dt>Cuándo</dt><dd>{conMayuscula(fechaLarga(turno.fecha))}, {turno.hora_inicio} a {turno.hora_fin}</dd></div>
          <div><dt>Servicio</dt><dd>{turno.servicio}</dd></div>
          <div><dt>Modalidad</dt><dd>{turno.modalidad === 'videoconsulta' ? 'Videoconsulta' : 'Presencial'}</dd></div>
          <div><dt>Valor</dt><dd>{pesos(turno.precio)}</dd></div>
          <div><dt>Estado</dt><dd><InsigniaEstadoTurno estado={turno.estado} /></dd></div>
          <div><dt>Código</dt><dd className="codigo">{turno.codigo}</dd></div>
        </dl>
      )}
    </Modal>
  );
}

function ModalBloqueo({ alCerrar, alGuardar }: { alCerrar: () => void; alGuardar: () => void }) {
  const { mostrarAviso } = useAvisos();
  const [desde, setDesde] = useState(hoyIso());
  const [hasta, setHasta] = useState(hoyIso());
  const [horaInicio, setHoraInicio] = useState('13:00');
  const [horaFin, setHoraFin] = useState('14:00');
  const [motivo, setMotivo] = useState<MotivoDeBloqueo>('almuerzo');
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      await consultorio.bloquear({ fecha_inicio: desde, fecha_fin: hasta, hora_inicio: horaInicio, hora_fin: horaFin, motivo });
      mostrarAviso({ tono: 'exito', titulo: 'Horario bloqueado', cuerpo: 'Nadie va a poder reservar en ese rango.' });
      alGuardar();
      alCerrar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos bloquear el horario. Probá de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal abierto alCerrar={alCerrar} titulo="Bloquear horario"
      pie={<>
        <Boton variante="secundario" onClick={alCerrar}>Cancelar</Boton>
        <Boton cargando={guardando} onClick={() => void guardar()}>Bloquear</Boton>
      </>}>
      <AvisoError>{error}</AvisoError>
      <div className="formulario">
        <div className="formulario__fila">
          <Entrada etiqueta="Desde el día" type="date" min={hoyIso()} value={desde}
            onChange={(e) => { setDesde(e.target.value); if (e.target.value > hasta) setHasta(e.target.value); }} />
          <Entrada etiqueta="Hasta el día" type="date" min={desde} value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
        <div className="formulario__fila">
          <Entrada etiqueta="Desde la hora" type="time" step={1800} value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)} />
          <Entrada etiqueta="Hasta la hora" type="time" step={1800} value={horaFin} onChange={(e) => setHoraFin(e.target.value)} />
        </div>
        <Selector etiqueta="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value as MotivoDeBloqueo)}
          opciones={MOTIVOS_DE_BLOQUEO.map((m) => ({ valor: m, texto: TEXTO_MOTIVO[m] }))} />
        <p className="nota">Si ya hay turnos reservados en ese rango, primero cancelalos o reprogramalos.</p>
      </div>
    </Modal>
  );
}
